// Pure decisions of the cancel page (a port of v1's BookingCancelClient and
// its views): which state the page is in, and what the refund comes to.

import type { ApiBooking, ApiCourse, SessionLike } from "../reschedule/apiTypes";

export type CancelPageData = {
  booking: ApiBooking;
  session?: SessionLike;
  course?: ApiCourse;
  canCancel: boolean;
  canReschedule: boolean;
  refundPercentage: number;
};

export type CancelState =
  /** The customer can cancel: the form. */
  | "can-cancel"
  /** The policy doesn't allow it (and maybe rescheduling instead). */
  | "cant-cancel"
  /** Already cancelled. */
  | "cancelled";

/** v1 showed "already cancelled" only for a cancelled AND refunded booking
 * the API still let them cancel, and "can't be refunded under our policy"
 * for every other cancelled one, which isn't what happened. Here a
 * cancelled booking says it's cancelled. */
export function cancelState(data: { booking?: Pick<ApiBooking, "cancelled" | "refunded">; canCancel: boolean }): CancelState {
  if (data.booking?.cancelled) return "cancelled";
  return data.canCancel ? "can-cancel" : "cant-cancel";
}

/** "Cancel bundle" for a bundle, else "Cancel booking". */
export function cancelTitle(booking: { bookingType?: string } | undefined): string {
  return booking?.bookingType === "bundle" ? "Cancel bundle" : "Cancel booking";
}

/** "1 guest" / "3 guests". */
export function guestsLabel(count: number | undefined): string {
  const n = Number(count ?? 0);
  return n === 1 ? "1 guest" : `${n} guests`;
}

/** Whether "can't cancel" also offers to reschedule (never for a flexitime
 * booking, which is rescheduled through the school). */
export function offersRescheduleInstead(booking: { bookingType?: string } | undefined, canReschedule: boolean): boolean {
  return canReschedule && booking?.bookingType !== "flexitime";
}

/** A request-to-book the school hasn't confirmed: no money was taken, so
 * cancelling withdraws it and nothing is refunded (the API's own rule). */
export function isUnconfirmedRequest(booking: { confirmation?: string } | undefined): boolean {
  return booking?.confirmation === "requested";
}

export type RefundLines = {
  /** What the customer paid by card, in pence. */
  card: number;
  /** What goes back to their card under the policy, in pence. */
  cardRefund: number;
};

/** What the refund comes to: the card part of what they paid, at the
 * policy's percentage (v1 reads the same field, `accounting.userCharged`;
 * gift card value and bundle credit are reinstated, not refunded). */
export function refundLines(booking: Pick<ApiBooking, "accounting"> | undefined, refundPercentage: number): RefundLines {
  const card = Math.max(0, Number(booking?.accounting?.userCharged ?? 0) || 0);
  const pct = Math.min(100, Math.max(0, Number(refundPercentage) || 0));
  return { card, cardRefund: Math.round((card * pct) / 100) };
}

/** The note above the refund lines. */
export function refundNote(booking: { usedBundleID?: string | null } | undefined): string {
  return booking?.usedBundleID
    ? "As this booking was made with a bundle, the bundle credit is returned to your account when you cancel. You can view and manage your bundles at any time."
    : "Any gift card value used to pay for this booking is reinstated (and its code reactivated) when you cancel. Gift cards are non-refundable but can be used on any event or product with us.";
}

export const CANCEL_MESSAGE_MAX = 500;

/** The body of PUT /widget/pages/bookings/:id/cancel (v1's). */
export function cancelPayload(message: string) {
  return { reason: "User cancelled", message: message.trim(), refundMethod: "card" as const };
}
